import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import ContentLoader from '../../../components/common/ContentLoader';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import { Modal, Button, Badge, Table } from 'react-bootstrap';
import { formatDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';

const TrashVendorDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [vendor, setVendor] = useState(null);
    const [packageSummary, setPackageSummary] = useState([]);
    const [listings, setListings] = useState({
        holidays: [],
        hotels: [],
        cabs: [],
        yatras: [],
        doctors: [],
        hospitals: []
    });

    const [selectedPackage, setSelectedPackage] = useState(null); // Hidden by default
    const [isHighlighting, setIsHighlighting] = useState(false);
    const section3Ref = React.useRef(null);

    const handleSelectPackage = (pkgKey) => {
        setSelectedPackage(pkgKey);
        setIsHighlighting(true);
        setTimeout(() => {
            setIsHighlighting(false);
        }, 1500);

        setTimeout(() => {
            if (section3Ref.current) {
                section3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    // Modals
    const [showRestoreVendorModal, setShowRestoreVendorModal] = useState(false);
    const [showPermDeleteVendorModal, setShowPermDeleteVendorModal] = useState(false);
    const [restoringVendor, setRestoringVendor] = useState(false);
    const [deletingVendor, setDeletingVendor] = useState(false);

    // Listing View Modal
    const [viewListingModal, setViewListingModal] = useState({ show: false, item: null, module: '' });

    // Single Listing Restore Modal
    const [restoreListingModal, setRestoreListingModal] = useState({
        show: false,
        item: null,
        module: '',
        vendorInTrash: false,
        submitting: false
    });

    // Single Listing Permanent Delete Modal
    const [permDeleteListingModal, setPermDeleteListingModal] = useState({
        show: false,
        item: null,
        module: '',
        submitting: false
    });

    const fetchDetail = useCallback(async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/trash/vendors/${id}`);
            if (response.data && response.data.success) {
                setVendor(response.data.vendor);
                setPackageSummary(response.data.packageSummary);
                setListings(response.data.listings);
            }
        } catch (error) {
            console.error('Error fetching trash vendor details:', error);
            toast.error('Failed to load trash vendor details.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    // Restore Vendor Handler
    const handleRestoreVendorSubmit = async () => {
        setRestoringVendor(true);
        try {
            const response = await superAdminApiClient.post(`/superadmin/trash/vendors/${id}/restore`);
            if (response.data && response.data.success) {
                toast.success('Vendor profile and all listings restored successfully!');
                setShowRestoreVendorModal(false);
                fetchDetail();
            }
        } catch (error) {
            console.error('Error restoring vendor:', error);
            toast.error(error.response?.data?.message || 'Failed to restore vendor');
        } finally {
            setRestoringVendor(false);
        }
    };

    // Permanent Delete Vendor Handler
    const handlePermDeleteVendorSubmit = async () => {
        setDeletingVendor(true);
        try {
            const response = await superAdminApiClient.delete(`/superadmin/trash/vendors/${id}/permanent`);
            if (response.data && response.data.success) {
                toast.success('Vendor permanently deleted.');
                setShowPermDeleteVendorModal(false);
                navigate('/superadmin/trash/vendors');
            }
        } catch (error) {
            console.error('Error permanently deleting vendor:', error);
            toast.error(error.response?.data?.message || 'Failed to delete vendor');
        } finally {
            setDeletingVendor(false);
        }
    };

    // Trigger Single Listing Restore Modal
    const handleOpenRestoreListing = (item, moduleKey) => {
        const isVendorInTrash = vendor && vendor.is_in_trash;
        setRestoreListingModal({
            show: true,
            item,
            module: moduleKey,
            vendorInTrash: isVendorInTrash,
            submitting: false
        });
    };

    // Submit Single Listing Restore
    const handleConfirmRestoreListing = async (restoreVendorIfDeleted = false) => {
        setRestoreListingModal(prev => ({ ...prev, submitting: true }));
        try {
            const response = await superAdminApiClient.post(`/superadmin/trash/listings/${restoreListingModal.item.id}/restore`, {
                module: restoreListingModal.module,
                restoreVendorIfDeleted
            });

            if (response.data && response.data.success) {
                toast.success(response.data.message || 'Listing restored successfully!');
                setRestoreListingModal({ show: false, item: null, module: '', vendorInTrash: false, submitting: false });
                fetchDetail();
            } else if (response.data && response.data.vendorInTrash) {
                setRestoreListingModal(prev => ({ ...prev, vendorInTrash: true, submitting: false }));
            }
        } catch (error) {
            console.error('Error restoring listing:', error);
            toast.error(error.response?.data?.message || 'Failed to restore listing');
            setRestoreListingModal(prev => ({ ...prev, submitting: false }));
        }
    };

    // Trigger Single Listing Permanent Delete Modal
    const handleOpenPermDeleteListing = (item, moduleKey) => {
        setPermDeleteListingModal({
            show: true,
            item,
            module: moduleKey,
            submitting: false
        });
    };

    // Submit Single Listing Permanent Delete
    const handleConfirmPermDeleteListing = async () => {
        setPermDeleteListingModal(prev => ({ ...prev, submitting: true }));
        try {
            const response = await superAdminApiClient.delete(`/superadmin/trash/listings/${permDeleteListingModal.item.id}/permanent`, {
                data: { module: permDeleteListingModal.module }
            });

            if (response.data && response.data.success) {
                toast.success('Listing permanently deleted.');
                setPermDeleteListingModal({ show: false, item: null, module: '', submitting: false });
                fetchDetail();
            }
        } catch (error) {
            console.error('Error deleting listing permanently:', error);
            toast.error(error.response?.data?.message || 'Failed to delete listing');
            setPermDeleteListingModal(prev => ({ ...prev, submitting: false }));
        }
    };

    // Get Listing Title helper
    const getListingTitle = (item, moduleKey) => {
        if (!item) return '-';
        if (moduleKey === 'holidays') return item.package_name;
        if (moduleKey === 'hotels') return item.hotel_name;
        if (moduleKey === 'cabs') return item.brand_name;
        if (moduleKey === 'yatras') return item.yatra_name;
        if (moduleKey === 'doctors') return item.doctor_name;
        if (moduleKey === 'hospitals') return item.hospital_name;
        return 'Listing #' + item.id;
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="container-fluid py-5">
                    <ContentLoader />
                </div>
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="page-content">
                <div className="container-fluid">
                    <div className="alert alert-danger">Vendor not found.</div>
                </div>
            </div>
        );
    }

    const currentListings = listings[selectedPackage] || [];

    return (
        <div className="page-contentss">
            <SEO title={`Trash Vendor Details - ${vendor.name || vendor.company_name}`} description="Trash Vendor Detail Page" />

            <div className="container-fluidss">
                {/* Header & Actions */}
                <div className="row mb-3 align-items-center">
                    <div className="col-md-6">
                        <h4 className="mb-0">
                            <i className={`ri-delete-bin-line me-2 ${vendor.is_in_trash ? 'text-danger' : 'text-warning'}`}></i>
                            {vendor.is_in_trash ? 'Trash Vendor Details: ' : 'Trashed Listings for: '}
                            <span className="text-dark">{vendor.company_name || vendor.name}</span>
                        </h4>
                    </div>
                    <div className="col-md-6 text-md-end mt-2 mt-md-0">
                        {vendor.is_in_trash ? (
                            <>
                                <Button
                                    variant="success"
                                    className="me-2"
                                    onClick={() => setShowRestoreVendorModal(true)}
                                >
                                    <i className="ri-restart-line me-1"></i>Restore Vendor
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => setShowPermDeleteVendorModal(true)}
                                >
                                    <i className="ri-delete-bin-7-line me-1"></i>Delete Permanently
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => navigate(`/superadmin/vendors/detail/${id}`)}
                                >
                                    <i className="ri-user-shared-line me-1"></i>View Vendor Detail
                                </Button>
                                <Badge bg="success" className="p-2 fs-13">Vendor Active</Badge>
                            </>
                        )}
                    </div>
                </div>

                {/* Section 1 – Vendor Information */}
                <div className="row">
                    <div className="col-12">
                        <div className="card border shadow-sm mb-4">
                            <div className="card-header bg-light">
                                <h5 className="card-title mb-0">
                                    <i className="ri-user-settings-line me-2 text-primary"></i>Section 1 – Vendor Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Vendor Name</label>
                                        <div className="fw-bold">{vendor.name || '-'}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Company Name</label>
                                        <div className="fw-bold">{vendor.company_name || '-'}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Email</label>
                                        <div>{vendor.email || '-'}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Phone</label>
                                        <div>{vendor.phone || '-'}</div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Deleted Date</label>
                                        <div>{vendor.deleted_at ? formatDate(vendor.deleted_at, 'DD-MM-YYYY HH:mm') : 'Not Deleted'}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Expiry Date (30 Days)</label>
                                        <div>{vendor.trash_expires_at ? formatDate(vendor.trash_expires_at, 'DD-MM-YYYY') : '-'}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Total Listings</label>
                                        <div>
                                            <span className="badge bg-secondary fs-13">{vendor.total_listings}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Login Status</label>
                                        <div>
                                            {vendor.is_in_trash ? (
                                                <Badge bg="danger" className="fs-12">Disabled (In Trash)</Badge>
                                            ) : (
                                                <Badge bg="success" className="fs-12">Enabled (Active)</Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2 – Package Summary */}
                <div className="row">
                    <div className="col-12">
                        <div className="card border shadow-sm mb-4">
                            <div className="card-header bg-light">
                                <h5 className="card-title mb-0">
                                    <i className="ri-folder-zip-line me-2 text-warning"></i>Section 2 – Package Summary
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <Table className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Package</th>
                                                <th>Total Listings</th>
                                                <th>Trash Listings</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {packageSummary.map(pkg => (
                                                <tr key={pkg.key} className={selectedPackage === pkg.key ? 'table-primary fw-bold' : ''}>
                                                    <td className="fw-semibold">
                                                        <i className="ri-folder-2-line me-2 text-muted"></i>{pkg.label}
                                                    </td>
                                                    <td>{pkg.count}</td>
                                                    <td>
                                                        {pkg.deleted_count > 0 ? (
                                                            <span className="badge bg-danger">{pkg.deleted_count} in trash</span>
                                                        ) : (
                                                            <span className="badge bg-light text-muted">0</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant={selectedPackage === pkg.key ? 'primary' : 'outline-primary'}
                                                            onClick={() => handleSelectPackage(pkg.key)}
                                                        >
                                                            {selectedPackage === pkg.key ? (
                                                                <>
                                                                    <i className="ri-check-line me-1"></i>Viewing Listings
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="ri-eye-line me-1"></i>View Listings
                                                                </>
                                                            )}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3 Guide Banner (when no package is selected) */}
                {!selectedPackage && (
                    <div className="alert alert-info border shadow-sm d-flex align-items-center mb-4 p-3">
                        <i className="ri-information-line fs-24 me-3 text-info"></i>
                        <div>
                            <h6 className="mb-1 text-info fw-bold">Package Listings Details</h6>
                            <div className="text-muted small">
                                Click <strong>"View Listings"</strong> on any package above (Holidays, Hotels, Cabs, Yatras, Doctors, Hospitals) to display its listings table here.
                            </div>
                        </div>
                    </div>
                )}

                {/* Section 3 – Package Listings View (Visible only when selectedPackage is set) */}
                {selectedPackage && (
                    <div className="row" ref={section3Ref}>
                        <div className="col-12">
                            <div className={`card border shadow-sm transition-all ${isHighlighting ? 'border-primary shadow-lg bg-soft-primary' : ''}`} style={{ transition: 'all 0.4s ease-in-out' }}>
                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0 text-capitalize text-primary">
                                        <i className="ri-list-check me-2 text-primary"></i>Section 3 – {selectedPackage} Package Listings
                                        {isHighlighting && (
                                            <span className="badge bg-warning text-dark ms-2">
                                                <i className="ri-flashlight-line me-1"></i>Selected
                                            </span>
                                        )}
                                    </h5>
                                    <div>
                                        <span className="badge bg-info me-2">
                                            {currentListings.length} Total Listings Found
                                        </span>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => setSelectedPackage(null)}
                                            title="Hide Section 3"
                                        >
                                            <i className="ri-close-line me-1"></i>Hide Section 3
                                        </Button>
                                    </div>
                                </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <Table className="align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Listing Title</th>
                                                <th>Category / Details</th>
                                                <th>Created Date</th>
                                                <th>Deleted Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentListings.length > 0 ? (
                                                currentListings.map(item => (
                                                    <tr key={item.id}>
                                                        <td className="fw-semibold">
                                                            {getListingTitle(item, selectedPackage)}
                                                        </td>
                                                        <td>
                                                            {item.city || item.cab_city || item.category || '-'}
                                                        </td>
                                                        <td>{item.created_at ? formatDate(item.created_at, 'DD-MM-YYYY') : '-'}</td>
                                                        <td>
                                                            {item.deleted_at ? (
                                                                <span className="text-danger">{formatDate(item.deleted_at, 'DD-MM-YYYY HH:mm')}</span>
                                                            ) : (
                                                                <span className="text-success">Active</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.deleted_at ? (
                                                                <Badge bg="danger">In Trash</Badge>
                                                            ) : (
                                                                <Badge bg="success">Active</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {/* View Read-Only Details */}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-info"
                                                                className="me-2"
                                                                onClick={() => setViewListingModal({ show: true, item, module: selectedPackage })}
                                                            >
                                                                <i className="ri-eye-line me-1"></i>View
                                                            </Button>

                                                            {/* Restore Listing */}
                                                            {item.deleted_at && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="success"
                                                                    className="me-2"
                                                                    onClick={() => handleOpenRestoreListing(item, selectedPackage)}
                                                                >
                                                                    <i className="ri-restart-line me-1"></i>Restore
                                                                </Button>
                                                            )}

                                                            {/* Delete Permanently */}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleOpenPermDeleteListing(item, selectedPackage)}
                                                            >
                                                                <i className="ri-delete-bin-line me-1"></i>Permanent Delete
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4 text-muted">
                                                        No listings found for package: <strong>{selectedPackage}</strong>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            </div>

            {/* Modal: View Listing Details (Read-Only) */}
            <Modal show={viewListingModal.show} onHide={() => setViewListingModal({ show: false, item: null, module: '' })} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Listing Details (Read-Only)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewListingModal.item && (
                        <div className="row g-3">
                            <div className="col-md-6">
                                <strong>Title: </strong> {getListingTitle(viewListingModal.item, viewListingModal.module)}
                            </div>
                            <div className="col-md-6">
                                <strong>Module: </strong> {viewListingModal.module}
                            </div>
                            <div className="col-md-6">
                                <strong>City / Location: </strong> {viewListingModal.item.city || viewListingModal.item.cab_city || '-'}
                            </div>
                            <div className="col-md-6">
                                <strong>Price: </strong> {viewListingModal.item.package_price || viewListingModal.item.hotel_price || viewListingModal.item.consultation_fees ? `₹${formatPrice(viewListingModal.item.package_price || viewListingModal.item.hotel_price || viewListingModal.item.consultation_fees)}` : '-'}
                            </div>
                            <div className="col-md-6">
                                <strong>Status: </strong> {viewListingModal.item.status || 'N/A'}
                            </div>
                            <div className="col-md-6">
                                <strong>Trash State: </strong> {viewListingModal.item.deleted_at ? 'In Trash' : 'Active'}
                            </div>
                            <div className="col-12">
                                <strong>Description / Overview: </strong>
                                <p className="bg-light p-2 rounded mt-1 border">
                                    {viewListingModal.item.description || viewListingModal.item.package_overview || viewListingModal.item.hotel_overview || viewListingModal.item.bio || 'No overview provided.'}
                                </p>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setViewListingModal({ show: false, item: null, module: '' })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: Restore Vendor Confirmation */}
            <Modal show={showRestoreVendorModal} onHide={() => setShowRestoreVendorModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Restore Vendor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-2">
                        <i className="ri-restart-line text-success fs-48 d-block mb-2"></i>
                        <h5>Restore Vendor Profile & All Listings?</h5>
                        <p className="text-muted">
                            Vendor: <strong>{vendor.name || vendor.company_name}</strong>
                        </p>
                        <p className="text-muted">
                            This will restore the vendor profile and all deleted listings across every package. Vendor login will be re-enabled and a restoration email will be sent.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRestoreVendorModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleRestoreVendorSubmit} disabled={restoringVendor}>
                        {restoringVendor ? 'Restoring...' : 'Restore All'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: Permanent Delete Vendor Confirmation */}
            <Modal show={showPermDeleteVendorModal} onHide={() => setShowPermDeleteVendorModal(false)}>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title className="text-white">Delete Permanently</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-2">
                        <i className="ri-error-warning-line text-danger fs-48 d-block mb-2"></i>
                        <h5 className="text-danger">Permanent Deletion Warning!</h5>
                        <p className="fw-bold">
                            Vendor: {vendor.name || vendor.company_name} (Total Listings: {vendor.total_listings})
                        </p>
                        <p className="text-danger small">
                            This action <strong>CANNOT</strong> be undone. All vendor information, package listings, uploaded media references, and associated activity records will be permanently deleted from the database.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPermDeleteVendorModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handlePermDeleteVendorSubmit} disabled={deletingVendor}>
                        {deletingVendor ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: Single Listing Restore (Handling Vendor in Trash requirement) */}
            <Modal show={restoreListingModal.show} onHide={() => setRestoreListingModal({ show: false, item: null, module: '', vendorInTrash: false, submitting: false })}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {restoreListingModal.vendorInTrash ? 'Vendor Restore Required' : 'Restore Listing'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {restoreListingModal.vendorInTrash ? (
                        <div className="text-center py-2">
                            <i className="ri-alert-line text-warning fs-48 d-block mb-2"></i>
                            <h5>Vendor Restore Required</h5>
                            <p className="text-muted">
                                This listing belongs to a vendor profile that is currently in Trash.
                            </p>
                            <p className="text-dark fw-semibold">
                                To restore this listing, the vendor profile must be restored first.
                            </p>
                        </div>
                    ) : (
                        <div className="text-center py-2">
                            <i className="ri-restart-line text-success fs-48 d-block mb-2"></i>
                            <h5>Restore Listing?</h5>
                            <p className="text-muted">
                                Title: <strong>{getListingTitle(restoreListingModal.item, restoreListingModal.module)}</strong>
                            </p>
                            <p className="text-muted">This will move the selected listing back to active status.</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setRestoreListingModal({ show: false, item: null, module: '', vendorInTrash: false, submitting: false })}>
                        Cancel
                    </Button>
                    {restoreListingModal.vendorInTrash ? (
                        <Button
                            variant="success"
                            onClick={() => handleConfirmRestoreListing(true)}
                            disabled={restoreListingModal.submitting}
                        >
                            {restoreListingModal.submitting ? 'Restoring...' : 'Restore Vendor & Listing'}
                        </Button>
                    ) : (
                        <Button
                            variant="success"
                            onClick={() => handleConfirmRestoreListing(false)}
                            disabled={restoreListingModal.submitting}
                        >
                            {restoreListingModal.submitting ? 'Restoring...' : 'Restore Listing'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal: Single Listing Permanent Delete Confirmation */}
            <Modal show={permDeleteListingModal.show} onHide={() => setPermDeleteListingModal({ show: false, item: null, module: '', submitting: false })}>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title className="text-white">Delete Listing Permanently</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-2">
                        <i className="ri-error-warning-line text-danger fs-48 d-block mb-2"></i>
                        <h5 className="text-danger">Permanent Delete Listing</h5>
                        <p className="fw-bold">
                            Title: {getListingTitle(permDeleteListingModal.item, permDeleteListingModal.module)}
                        </p>
                        <p className="text-muted small">
                            This listing and its associated image files will be permanently deleted. This action cannot be undone.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPermDeleteListingModal({ show: false, item: null, module: '', submitting: false })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmPermDeleteListing} disabled={permDeleteListingModal.submitting}>
                        {permDeleteListingModal.submitting ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default TrashVendorDetail;
